<template>
  <div class="row">
    <div :class="optionsClass">
      <div class="btn-group w-100" role="group" aria-label="Risk option">
        <radio-button
          v-for="(item, i) in riskLevelDetails"
          :ref="'riskOption' + mode"
          v-bind:key="i"
          :name="getRadioButtonGroupName()"
          :label="getRiskLevelLabel(item)"
          :value="riskLevel"
          @change="onChangeRiskOption($event)"
        />
      </div>
    </div>
    <div v-if="canFineTune" class="col-sm-3 mt-3 mt-sm-0 d-flex align-items-end">
      <div class="input-group">
        <button
          ref="decreaseRate"
          class="btn btn-outline-secondary"
          type="button"
          name="decreaseRate"
          @click="onDecreaseRateClick"
          :aria-label="decreaseButtonAria"
        >
          -
        </button>
        <input
          ref="returnRate"
          type="text"
          class="form-control text-center mt-0"
          placeholder=""
          aria-label="Estimated annual growth percentage"
          value="3.5%"
          disabled
        />
        <button
          ref="increaseRate"
          class="btn btn-outline-secondary"
          type="button"
          name="increaseRate"
          @click="onIncreaseRateClick"
          :aria-label="increaseButtonAria"
        >
          +
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import RadioButton from "@/shared/components/RadioButton.vue";
import { app } from "@/guiide/ts/app";
import { RiskConfiguration } from "@/guiide/ts/configuration/riskConfiguration";
import { RiskLevelDetail } from "@/guiide/ts/configuration/riskLevelDetail";
import Browser from "@/shared/ts/browser";

export default defineComponent({
  name: "RiskSelector",
  components: {
    RadioButton
  },
  props: {
    mode: { type: String, required: true },
    ariaLabel: { type: String, default: "" }
  },
  setup() {
    const returnRate = ref(null);
    const decreaseRate = ref(null);
    const increaseRate = ref(null);
    return {
      returnRate,
      decreaseRate,
      increaseRate
    };
  },
  data(): { canFineTune: boolean; riskLevelDetails: Array<RiskLevelDetail>; maxRisk: number } {
    return {
      canFineTune: true,
      riskLevelDetails: new Array<RiskLevelDetail>(),
      maxRisk: 7,
    };
  },
  mounted() {
    const risk = this.$store.state.configuration.risk as RiskConfiguration;
    this.canFineTune = risk.canFineTuneReturnRates;
    this.riskLevelDetails = [risk.none, risk.low, risk.medium, risk.high];
    this.maxRisk = risk.high.max;
  },
  watch: {
    returnLevel(newValue) {
      if (this.canFineTune) {
        this.returnRate.value = newValue + '%';
      }
    }
  },
  computed: {
    returnLevel: function(): number {
      if (this.mode === "Pensions") {
        return this.$store.state.retiree?.returnOnPensions ?? 3;
      } else {
        return this.$store.state.retiree?.returnOnSavings ?? 3;
      }
    },
    riskLevel: function(): string {
      const useAbbreviation = Browser.isMobile();
      for (const riskLevelDetail of this.riskLevelDetails) {
        if (this.returnLevel <= riskLevelDetail.max) {
          return useAbbreviation
            ? riskLevelDetail.abbrev
            : riskLevelDetail.label;
        }
      }
      return "";
    },
    optionsClass: function(): string {
      return this.canFineTune ? "col-sm-9" : "col";
    },
    decreaseButtonAria: function(): string {
      return this.getAriaChangeText("Decrease");
    },
    increaseButtonAria: function(): string {
      return this.getAriaChangeText("Increase");
    }
  },
  methods: {
    getAriaChangeText(changeDirection: string): string {
      let assetType = this.mode === "Pensions" ? "defined contribution pension pots" : "savings pots";
      return `${changeDirection} the estimated annual growth in your ${assetType} by 0.5% from ${this.returnLevel}%.`;
    },
    getRadioButtonGroupName() {
      return this.mode === "Pensions"
        ? "PensionRiskOption"
        : "SavingRiskOption";
    },
    getRiskLevelLabel(riskLevel: RiskLevelDetail) {
      const useAbbreviation = Browser.isMobile();
      return useAbbreviation ? riskLevel.abbrev : riskLevel.label;
    },
    onChangeRiskOption(event) {
      const useAbbreviation = Browser.isMobile();
      for (const riskLevelDetail of this.riskLevelDetails) {
        const riskLevelDetailLabel = useAbbreviation
          ? riskLevelDetail.abbrev
          : riskLevelDetail.label;
        if (riskLevelDetailLabel == event) {
          this.updateRate(riskLevelDetail.default);
          return;
        }
      }
    },
    async onDecreaseRateClick() {
      const newRate = this.returnLevel - 0.5;
      if (newRate < 0) {
        return;
      }
      await this.updateRate(newRate);
    },
    async onIncreaseRateClick() {
      const newRate = this.returnLevel + 0.5;
      if (newRate > this.maxRisk) {
        return;
      }
      await this.updateRate(newRate);
    },
    async updateRate(newRate: number) {
      if (this.mode === "Pensions") {
        await app.updatePensionsRate(newRate);
      } else {
        await app.updateSavingsRate(newRate);
      }
    },
    setDisabledState(disabled: boolean) {
      if (this.canFineTune) {
        this.decreaseRate.disabled = disabled;
        this.increaseRate.disabled = disabled;
      }

      this.setRadioButtonDisableState(0, disabled);
      this.setRadioButtonDisableState(1, disabled);
      this.setRadioButtonDisableState(2, disabled);
      this.setRadioButtonDisableState(3, disabled);
    },
    setRadioButtonDisableState(radioButtonIndex: number, disabled: boolean) {
      const radioButton =
        this.$refs["riskOption" + this.mode][radioButtonIndex];
      radioButton.setDisabledState(disabled);
    }
  }
});
</script>

<style scoped>
    .btn-group {
        height: 100%;
    }

    .input-group > input {
        padding: 0;
    }
</style>
